import { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Phone',
      value: '+91 9686777558',
      href: 'tel:+919686777558',
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'ragunath.v@riyaalcobev.com',
      href: 'mailto:info@riyaalcobev.com',
    },
    {
      icon: MapPin,
      label: 'Location',
      value: 'India',
      href: null,
    },
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section data-testid="contact-hero" className="relative py-20 lg:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-4">Contact Us</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
              Let's Start a<br />
              <span className="text-blue-400">Conversation</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-300 leading-relaxed">
              Ready to transform your manufacturing operations? Get in touch to 
              discuss how we can help your organization achieve operational excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section data-testid="contact-form-section" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="font-serif text-3xl font-semibold text-slate-900 mb-4">
                  Get in Touch
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Have questions about our services? We'd love to hear from you. 
                  Reach out using any of the methods below or fill out the form.
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4 p-6 bg-slate-50 border border-slate-100"
                  >
                    <div className="w-12 h-12 bg-blue-600 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">{item.label}</p>
                      {item.href ? (
                        <a 
                          href={item.href}
                          className="text-lg font-medium text-slate-900 hover:text-blue-600 transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-lg font-medium text-slate-900">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Response Promise */}
              <div className="p-6 bg-slate-900 text-white">
                <h3 className="font-serif text-xl font-semibold mb-3">Quick Response Guaranteed</h3>
                <p className="text-slate-300 leading-relaxed">
                  We typically respond to all inquiries within 24 hours. For urgent matters, 
                  please call directly.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              {isSubmitted ? (
                <div data-testid="success-message" className="text-center py-16 px-8 bg-slate-50 border border-slate-100">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-4">
                    Message Sent Successfully!
                  </h3>
                  <p className="text-slate-600 mb-8 max-w-md mx-auto">
                    Thank you for reaching out. We will review your message and get back to you shortly.
                  </p>
                  <Button 
                    onClick={() => setIsSubmitted(false)}
                    className="bg-slate-900 text-white hover:bg-slate-800 rounded-none px-8 py-3 text-sm font-semibold uppercase tracking-wider"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                        className="rounded-none border-slate-200 focus:border-blue-500 focus:ring-blue-500 h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                        Email Address <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="rounded-none border-slate-200 focus:border-blue-500 focus:ring-blue-500 h-12"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="contact-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                        className="rounded-none border-slate-200 focus:border-blue-500 focus:ring-blue-500 h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-sm font-medium text-slate-700">
                        Company/Organization
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        data-testid="contact-company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your company name"
                        className="rounded-none border-slate-200 focus:border-blue-500 focus:ring-blue-500 h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-sm font-medium text-slate-700">
                      Message <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your requirements or questions..."
                      rows={6}
                      className="rounded-none border-slate-200 focus:border-blue-500 focus:ring-blue-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={isSubmitting}
                    className="w-full md:w-auto bg-blue-600 text-white hover:bg-blue-700 rounded-none px-10 py-4 text-sm font-bold uppercase tracking-wider btn-shadow disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map/Location Section */}
      <section data-testid="location-section" className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">Our Reach</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6">
              Serving Clients Across India
            </h2>
            <p className="text-slate-600 leading-relaxed max-w-2xl mx-auto">
              We provide consulting services to AlcoBev and FMCG organizations across India, 
              with expertise in multi-plant manufacturing environments and supply chain operations.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
